import React, { useContext, useState, useCallback, useEffect } from 'react';
import { AppContext } from 'src/AppContext';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';

import ProfileFilter from './profiles/ProfileFilter';
import ProfileContent from './profiles/ProfileContent';

import { gray } from 'src/templates/shared-theme/themePrimitives';
import { listProfiles } from 'src/api/api';

const VIEW_OPTIONS = [
  { value: 'list', icon: <Iconify icon="carbon:list-boxes" /> },
  { value: 'grid', icon: <Iconify icon="carbon:grid" /> },
];

const PER_FETCH_LIMIT = 8;
const MAX_FETCH_WITHOUT_LOGIN = 8;

export default function Profiles() {
  const mobileOpen = useBoolean();
  const loading = useBoolean(true);
  const { user } = useContext(AppContext);
  // const [viewMode, setViewMode] = useState('grid');

  const [profiles, setProfiles] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [profileFilter, setProfileFilter] = useState(null);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    msg: "",
  });

  // const handleChangeViewMode = useCallback(
  //   (event, newAlignment) => {
  //     if (newAlignment !== null) {
  //       setViewMode(newAlignment);
  //     }
  //   },
  //   []
  // );

  const filterToQuery = (filterValues = null) => {
    if (!filterValues) {
      filterValues = profileFilter;
    }

    const joinFn = (slice) => "[" + slice.join(",") + "]";

    let query = {};
    if (filterValues) {
      if (filterValues.filterExpertise.length > 0) {
        query.expertise = filterValues.filterExpertise[0];
      }
      if (filterValues.filterPrice.length > 0) {
        query.prices = joinFn(filterValues.filterPrice);
      }
      if (filterValues.filterTime.length > 0) {
        query.time = filterValues.filterTime[0];
      }
      if (filterValues.filterRegion.length > 0) {
        query.region = filterValues.filterRegion[0];
      }
      if (filterValues.filterLanguage.length > 0) {
        query.languages = joinFn(filterValues.filterLanguage);
      }
    }
    return query;
  };

  const onChangeFilter = async (values) => {
    console.log(values);
    setProfileFilter(values);
    onRefresh(values);
  };

  const onLoadMore = async () => {
    const resp = await listProfiles({
      limit: PER_FETCH_LIMIT,
      offset: profiles?.offset || 0,
      ...filterToQuery(),
    });
    if (resp.error) {
      console.error("onLoadMore", resp);
      return;
    }

    if (profiles) {
      if (resp.offset == profiles.offset || !user && resp.offset > MAX_FETCH_WITHOUT_LOGIN) {
        setHasMore(false);
      }
    }
    setProfiles({
      profiles: [...(profiles?.profiles || []), ...resp.profiles],
      offset: resp.offset,
    })
    loading.onFalse();
  };

  const onRefresh = async (filterValues = null) => {
    const resp = await listProfiles({
      limit: PER_FETCH_LIMIT,
      offset: 0,
      ...filterToQuery(filterValues),
    });
    if (resp.error) {
      console.error("onRefresh", resp);
      return;
    }

    setHasMore(resp.offset >= PER_FETCH_LIMIT);
    setProfiles(resp);
    loading.onFalse();
  };

  const openSnackBar = (msg) => {
    setSnackBarConfig({
      open: true,
      msg: msg,
    });
  };

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarConfig({
      open: false,
      msg: "",
    });
  };

  const action = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackBarClose}
        sx={{
          background: gray[700],
          border: 'none',
          color: 'white',
          '&:hover': {
            background: gray[600],
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  useEffect(() => {
    onLoadMore();
  }, []);

  return (
    <Container
      sx={{ mt: 12 }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
        open={snackBarConfig.open}
        onClose={handleSnackBarClose}
        message={snackBarConfig.msg}
        action={action}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          py: 5,
        }}
      >
        <Typography variant="h3">Trainer Profiles</Typography>

        <Button
          color="inherit"
          variant="contained"
          startIcon={<Iconify icon="carbon:filter" width={18} />}
          onClick={mobileOpen.onTrue}
          sx={{
            display: { md: 'none' },
          }}
        >
          Filters
        </Button>
      </Stack>

      <Stack
        direction={{
          xs: 'column-reverse',
          md: 'row',
        }}
        sx={{ mb: { xs: 8, md: 10 } }}
      >
        <ProfileFilter
          open={mobileOpen.value}
          onClose={mobileOpen.onFalse}
          onChangeFilter={onChangeFilter}
          openSnackBar={openSnackBar}
        />

        <Box
          sx={{
            flexGrow: 1,
            pl: { md: 8 },
            width: { md: `calc(100% - ${280}px)` },
          }}
        >
          {/* <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 5 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={handleChangeViewMode}
              sx={{ borderColor: 'transparent' }}
            >
              {VIEW_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.icon}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack> */}

          <ProfileContent
            loading={loading.value}
            // viewMode={viewMode}
            profiles={profiles}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            onRefresh={onRefresh}
          />
        </Box>
      </Stack>
    </Container>
  )
}